import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { getSupplierById, updateSupplier } from '../../services/supplierService';

// Material UI components
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  AlertTitle,
  Breadcrumbs,
  Link,
  CircularProgress,
  Divider
} from '@mui/material';

// Material UI icons
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';

const EditSupplier = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        setError(null);

        const supplierData = await getSupplierById(id);

        setFormData({
          name: supplierData.name || '',
          contact_person: supplierData.contact_person || '',
          phone: supplierData.phone || '',
          email: supplierData.email || '',
          address: supplierData.address || ''
        });

        setLoading(false);
      } catch (err) {
        console.error('Error fetching supplier:', err);
        setError(err.message || 'Failed to load supplier details');
        setLoading(false);
      }
    };

    fetchSupplier();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim()) {
      setError('Supplier name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Prepare data - remove empty fields to avoid validation issues
      const supplierData = { ...formData };

      // Remove empty fields
      Object.keys(supplierData).forEach(key => {
        if (supplierData[key] === '') {
          delete supplierData[key];
        }
      });

      await updateSupplier(id, supplierData);

      // Redirect to supplier details on success
      navigate(`/suppliers/${id}`, {
        state: {
          notification: {
            message: 'Supplier updated successfully',
            severity: 'success'
          }
        }
      });
    } catch (err) {
      console.error('Error updating supplier:', err);
      setError(err.message || 'Failed to update supplier. Please try again.');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to="/suppliers"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <BusinessIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Suppliers
        </Link>
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to={`/suppliers/${id}`}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Supplier Details
        </Link>
        <Typography
          color="text.primary"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Edit Supplier
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Edit Supplier
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Supplier Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Supplier Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                helperText="Enter the official name of the supplier company"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                helperText="Physical address of the supplier"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Person"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                helperText="Primary contact person at the supplier"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                helperText="Contact phone number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                helperText="Contact email address"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CancelIcon />}
            component={RouterLink}
            to={`/suppliers/${id}`}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default EditSupplier;
